import type { FlatContentLayersType } from "@config";
import type { ITableCopiedCell } from "@library-elements/table";
import { serializeCellRange } from "./serialize-cell-range";

const stripHtml = (html: string): string => {
  // DOMParser is safer than mounting markup on a live element — the latter
  // still triggers resource fetches (e.g. <img src=…>) while parsing.
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent ?? "";
};

const escapeHtml = (text: string): string =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const buildHTMLTableFromCells = (
  cells: ITableCopiedCell[],
): string => {
  if (cells.length === 0) return "";

  const minRow = Math.min(...cells.map((c) => c.row));
  const maxRow = Math.max(...cells.map((c) => c.row));
  const minCol = Math.min(...cells.map((c) => c.col));
  const maxCol = Math.max(...cells.map((c) => c.col));

  let html = "<table>";
  for (let r = minRow; r <= maxRow; r++) {
    html += "<tr>";
    for (let c = minCol; c <= maxCol; c++) {
      const cell = cells.find((cl) => cl.row === r && cl.col === c);
      const content = cell ? escapeHtml(stripHtml(cell.data.content)) : "";
      html += `<td>${content}</td>`;
    }
    html += "</tr>";
  }
  html += "</table>";
  return html;
};

export const copyTableCellsToClipboard = (
  id: string,
  cellsIds: string[],
  flatContentLayers: FlatContentLayersType,
) => {
  const payload = serializeCellRange(id, cellsIds, flatContentLayers);
  if (!payload) return;

  const jsonClipboard = JSON.stringify(payload);
  const htmlTable = buildHTMLTableFromCells(payload.cells);

  navigator.clipboard
    .write([
      new ClipboardItem({
        "text/plain": new Blob([jsonClipboard], { type: "text/plain" }),
        "text/html": new Blob([htmlTable], { type: "text/html" }),
      }),
    ])
    .catch(() => {
      // Fallback for browsers that don't support clipboard.write()
      navigator.clipboard.writeText(jsonClipboard);
    });
};
